import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { parse, startOfDay, isBefore, isValid, isEqual, addDays } from "date-fns";
import * as MHLSalesService from '../../Services/MHLSales/MHLSalesService';
import toast from "react-hot-toast";
import { FaClock, FaEye, FaSpinner, FaFileInvoice, FaUsers, FaMoneyBillWave, FaWallet, FaBalanceScale, FaExchangeAlt, FaArrowRight } from "react-icons/fa";
import Popover from '../../Common/Popper';
import '../../Dashboard.css';
import "bootstrap/dist/css/bootstrap.min.css";
import { AlarmClock } from "lucide-react";
import TestAndRevenueComponent from "../Dashboard/MHL_TestAndRevenueComponent";

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';

const MHLSalesComponent = () => {
    const location = useLocation();
    const { encodedData } = useParams();
    const [clientCode, setClientCode] = useState(null);
    const [isValidLink, setIsValidLink] = useState(false);

    //AccountDetails
    const [accountDetails, setAccountDetails] = useState([]);
    const [mainLoader, setMainLoader] = useState(false);

    //Test&Revenue
    const [topTestDetails, setTopTestDetails] = useState([]);
    const [selectedRevenueMonth, setSelectedRevenueMonth] = useState(() =>
        new Date().toLocaleString('en-US', { month: 'short', year: 'numeric' }).toString());
    const [testAndRevenueLoader, setTestAndRevenueLoader] = useState(false);

    //VisitSummary
    const [visitSummaryLoader, setVisitSummaryLoader] = useState(false);
    const [ReportSerachReportGenrateData, setReportSerachReportGenrateData] = useState(0);
    const [ReportSerachTransactionsData, setReportSerachTransactionsData] = useState(0);
    const [ReportDelayedData, setReportDelayedData] = useState([]);

    //InvoiceList
    const [CCInvoiceOutstandingData, setCCInvoiceOutstandingData] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [invoiceLoader, setInvoiceLoader] = useState(false);

    //RATransactionCard
    const [RATransactionCardData, setRATransactionCardData] = useState([]);
    const [raTransactionLoader, setRATransactionLoader] = useState(false);

    //BarChart
    const [barChartLoader, setBarChartLoader] = useState(false);
    const [ChartData, setChartData] = useState([]);
    const [viewMode, setViewMode] = useState('Revenue'); // Default is Revenue

    //BarChart
    const [barChartVisitsLoader, setBarChartVisitsLoader] = useState(false);
    const [ChartVisitsData, setChartVisitsData] = useState([]);
    const [viewVisitsMode, setViewVisitsMode] = useState('Revenue'); // Default is Revenue

    // === Session Storage with TTL ===
    const setWithExpiry = (key, value, ttlMs) => {
        const now = new Date();
        const item = {
            value,
            expiry: now.getTime() + ttlMs,
        };
        sessionStorage.setItem(key, JSON.stringify(item));
    };

    const getWithExpiry = (key) => {
        const itemStr = sessionStorage.getItem(key);
        if (!itemStr) return null;
        try {
            const item = JSON.parse(itemStr);
            if (!item.expiry || new Date().getTime() > item.expiry) {
                sessionStorage.removeItem(key);
                return null;
            }
            return item.value;
        } catch {
            sessionStorage.removeItem(key);
            return null;
        }
    };
    // === End TTL ===


    // const storageKey = `MHLSalesData_${encodedData}_${selectedRevenueMonth}`;

    // useEffect(() => {
    //     if (clientCode) {
    //         const key = `MHLSalesData_${encodedData}_${selectedRevenueMonth}`;
    //         const cached = sessionStorage.getItem(key);

    //         if (cached) {
    //             const parsed = JSON.parse(cached);
    //             setTopTestDetails(parsed);
    //             //console.log("Loaded from sessionStorage:", parsed);
    //         } else {
    //             GetTestAndRevenueDetails(clientCode, key);
    //         }
    //     }
    // }, [selectedRevenueMonth, encodedData, clientCode]);

    const CustomXAxisTick = ({ x, y, payload }) => {
        const fullText = payload.value;

        const displayText = fullText.length > 12 ? `${fullText.slice(0, 12)}...` : fullText;
        return (
            <g transform={`translate(${x},${y})`}>
                <title>{fullText}</title>
                <text
                    x={0}
                    y={0}
                    dy={16}
                    textAnchor="end"
                    fill="#8884d8"
                    fontSize={10}
                    fontWeight="bold"
                    transform="rotate(-45)"
                >
                    {displayText}
                </text>
            </g>
        );
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div
                    style={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        padding: '6px 10px 5px 10px',
                        borderRadius: '8px',
                        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                        fontSize: "11px",
                    }}
                >
                    <p style={{ margin: 0, padding: 0 }}><strong>Test Name:</strong> {data.TestName}</p>
                    <p style={{ margin: 0 }}><strong>{data.tooltipLabel}:</strong> {payload[0].value}</p>
                </div>
            );
        }
        return null;
    };

    const VisitCustomTooltip = ({ active, payload, viewVisitsMode }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const labelName = viewVisitsMode === "Count" ? "Count" : "Revenue";

            return (
                <div
                    style={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        padding: '6px 10px 5px 10px',
                        borderRadius: '8px',
                        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                        fontSize: "11px",
                    }}
                >
                    <p style={{ margin: 0, padding: 0 }}><strong>Period:</strong> {data.MonthYear}</p>
                    <p style={{ margin: 0 }}><strong>{labelName}:</strong> {payload[0].value}</p>
                </div>
            );
        }
        return null;
    };

    useEffect(() => {
        document.body.style.backgroundColor = "white";
        return () => (document.body.style.backgroundColor = "");
    }, []);

    useEffect(() => {
        if (!encodedData) {
            setIsValidLink(false);
            return;
        }

        try {
            const [code, dateStr] = atob(encodedData).split("|");

            const parsedDate = parse(dateStr, "yyyy-MM-dd", new Date());
            const today = startOfDay(new Date());
            const tomorrow = addDays(today, 1);

            const isExpired =
                !code ||
                !isValid(parsedDate) ||
                (!isEqual(parsedDate, today) && !isEqual(parsedDate, tomorrow));

            if (isExpired) {
                setIsValidLink(false);
                return;
            }

            setClientCode(code);
            setIsValidLink(true);
            fetchAccountDetails(code); // Your API call
        } catch {
            setIsValidLink(false);
        }
    }, [encodedData]);

    const fetchAccountDetails = (code) => {
        const key = `MHLSales_Account_${encodedData}`;
        const cached = getWithExpiry(key);
        if (cached) {
            setAccountDetails(cached);
            //console.log("Loaded AccountDetails from sessionStorage (TTL)");
            loadExtrasFromAccount(cached, code);
            return;
        }

        setMainLoader(true);
        MHLSalesService.GetAccountDetails(code)
            .then(({ data }) => {
                if (data.IsSuccess) {
                    setAccountDetails(data.Data);
                    setWithExpiry(key, data.Data, 120 * 60 * 1000);
                    loadExtrasFromAccount(data.Data, code);
                } else {
                    setAccountDetails([]);
                }
            })
            .catch(() => toast.error("Something went wrong."))
            .finally(() => setMainLoader(false));
    };

    // 🔁 Reusable logic
    const loadExtrasFromAccount = (data, code) => {
        if (code !== null && code !== undefined && code !== "") {
            // if(code)
            GetBarChartVisitDetails(code);
            GetTestAndRevenueDetails(code);
            if (data.MHL_Payment_Type__c === "Rolling Advance") {
                GetRA_TransactionCard(code);
            }
            if (data.MHL_Payment_Type__c === "Credit") {
                InvoiceDetailList(code);
            }
        }
    };


    useEffect(() => {
        GetTestAndRevenueDetails(clientCode);
    }, [selectedRevenueMonth]);

    const GetTestAndRevenueDetails = (code) => {
        const key = `MHLSales_TestRevenue_${encodedData}_${selectedRevenueMonth}`;
        const cached = getWithExpiry(key);
        if (cached) {
            setTopTestDetails(cached);
            //console.log("Loaded TestRevenue from sessionStorage (TTL)");
            return;
        }

        setTestAndRevenueLoader(true);
        MHLSalesService.GetTestAndRevenueDetails(code, selectedRevenueMonth)
            .then(({ data }) => {
                if (data.IsSuccess) {
                    setTopTestDetails(data.Data);
                    setWithExpiry(key, data.Data, 120 * 60 * 1000); // 30 minutes
                } else {
                    setTopTestDetails([]);
                }
            })
            .catch(() => toast.error("Something went wrong."))
            .finally(() => setTestAndRevenueLoader(false));
    };
    const InvoiceDetailList = (code) => {
        const key = `MHLSales_Invoice_${encodedData}`;
        const cached = getWithExpiry(key);
        if (cached) {
            setCCInvoiceOutstandingData(cached);
            setInvoices(cached?.invDetails?.slice(0, 3) || []);
            //console.log("Loaded Invoice from sessionStorage (TTL)");
            return;
        }

        setInvoiceLoader(true);
        MHLSalesService.InvoiceDetailList(code)
            .then(({ data }) => {
                if (data.IsSuccess) {
                    setCCInvoiceOutstandingData(data.Data);
                    setInvoices(data?.Data?.invDetails?.slice(0, 3) || []);
                    setWithExpiry(key, data.Data, 120 * 60 * 1000);
                } else {
                    setCCInvoiceOutstandingData({});
                }
            })
            .catch(() => toast.error("Something went wrong."))
            .finally(() => setInvoiceLoader(false));
    };


    const GetBarChartTestDetails = (code) => {
        setBarChartLoader(true);
        setChartData([])
        MHLSalesService.GetBarChartTestDetails(code)
            .then(({ data }) => {
                if (data.IsSuccess) {
                    //console.log("Barchart:", data.Data)
                    const formatted = data.Data.map(item => ({
                        ...item,
                        tooltipLabel: viewMode === 'Count' ? 'Count' : 'Revenue'
                    }));
                    setChartData(formatted);
                }
                else {
                    setChartData([])
                }
            })
            .catch(() => toast.error("Something went wrong."))
            .finally(() => {
                setBarChartLoader(false);
            });
    };

    const GetBarChartVisitDetails = (code) => {
        const key = `MHLSales_VisitChart_${encodedData}`;
        const cached = getWithExpiry(key);
        if (cached) {
            setChartVisitsData(cached);
            //console.log("Loaded Visit Chart from sessionStorage (TTL)");
            return;
        }

        setBarChartVisitsLoader(true);
        MHLSalesService.GetBarChartVisitDetails(code)
            .then(({ data }) => {
                if (data.IsSuccess) {
                    const formatted = data.Data.map(item => ({
                        ...item,
                        tooltipLabel: viewVisitsMode === 'Count' ? 'Count' : 'Revenue'
                    }));
                    setChartVisitsData(formatted);
                    setWithExpiry(key, formatted, 120 * 60 * 1000);
                } else {
                    setChartVisitsData([]);
                }
            })
            .catch(() => toast.error("Something went wrong."))
            .finally(() => setBarChartVisitsLoader(false));
    };

    const GetRA_TransactionCard = (code) => {
        const key = `MHLSales_RA_${encodedData}`;
        const cached = getWithExpiry(key);
        if (cached) {
            setRATransactionCardData(cached);
            //console.log("Loaded RA Card from sessionStorage (TTL)");
            return;
        }

        setRATransactionLoader(true);
        MHLSalesService.GetRA_TransactionCard(code)
            .then(({ data }) => {
                if (data.IsSuccess) {
                    setRATransactionCardData(data.Data);
                    setWithExpiry(key, data.Data, 120 * 60 * 1000);
                } else {
                    setRATransactionCardData([]);
                }
            })
            .catch(() => toast.error("Something went wrong."))
            .finally(() => setRATransactionLoader(false));
    };

    return (
        <div className="p-3 mb-0">

            {isValidLink ? (<div>

                <h3>Client Code{clientCode && (<small>: {clientCode}</small>)}</h3>

                {mainLoader ? (
                    <div className='centered-sales-message-loader'>
                        <FaSpinner style={{ width: "50px", height: "50px", color: "green" }} className="spinner-spin text-dark-500" />
                    </div>

                ) : (

                    <div className="row g-3 px-2 py-2 mt-2 mb-4 border rounded shadow-sm bg-white">

                        {/* Account Details */}
                        {/* <div className="col-md-6 col-lg-3" style={{
                            fontSize: accountDetails?.MHL_Payment_Type__c === "Credit" ? "13px" : "12px"
                        }}>
                            <div className="bg-white dashboard-card dashboard-card-enhanced rounded shadow-sm h-100 p-3">
                                <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center mb-2">
                                    <h5 className="mb-0">Account Details</h5>
                                </div>
                                <div>
                                    {accountDetails?.Name && (
                                        <div className="mb-2">
                                            <i className="fas fa-user me-2 text-dark"></i>
                                            <strong>Account Name:</strong> <span className="text-muted">{accountDetails.Name}</span>
                                        </div>
                                    )}
                                    {accountDetails?.MHL_Email__c && (
                                        <div className="mb-2">
                                            <i className="fas fa-envelope me-2 text-dark"></i>
                                            <strong>Email:</strong> <span className="text-muted">{accountDetails.MHL_Email__c}</span>
                                        </div>
                                    )}
                                    {accountDetails?.MHL_Mobile_Number__c && (
                                        <div className="mb-2">
                                            <i className="fas fa-phone me-2 text-dark"></i>
                                            <strong>Mobile No:</strong> <span className="text-muted">{accountDetails.MHL_Mobile_Number__c}</span>
                                        </div>
                                    )}
                                    {accountDetails?.MHL_Primary_Org_new__c && (
                                        <div className="mb-2">
                                            <i className="fas fa-building me-2 text-dark"></i>
                                            <strong>Primary Org:</strong> <span className="text-muted">{accountDetails.MHL_Primary_Org_new__c}</span>
                                        </div>
                                    )}
                                    {accountDetails?.MHL_Payment_Type__c && (
                                        <div className="mb-2">
                                            <i className="fas fa-credit-card me-2 text-dark"></i>
                                            <strong>Payment Type:</strong> <span className="text-muted">{accountDetails.MHL_Payment_Type__c}</span>
                                        </div>
                                    )}
                                    {(accountDetails?.Account_Record_Type__c || accountDetails?.MHL_Business_Type_B2C__c) && (
                                        <div className="mb-2">
                                            <i className="fas fa-briefcase me-2 text-dark"></i>
                                            <strong>Partner Type:</strong>{" "}
                                            <span className="text-muted">
                                                {accountDetails.Account_Record_Type__c || "-"}
                                                {accountDetails?.MHL_Business_Type_B2C__c ? " | " + accountDetails.MHL_Business_Type_B2C__c : ""}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div> */}



                        {/* VisitSummary */}
                        {/* <div className="col-md-6 col-lg-4">
                            <div className="bg-white dashboard-card dashboard-card-enhanced rounded shadow-sm p-3 h-100">
                                <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center mb-3">
                                    <h5 className="mb-0">Visit Summary
                                        <small className="text-dark fw-bold ml-1" style={{ fontSize: '0.7rem' }}>
                                            ({new Date().toLocaleString('en-US', { month: 'short' }) + '-' + String(new Date().getFullYear()).slice(-2)})
                                        </small>
                                    </h5>
                                </div>

                                {visitSummaryLoader ? (
                                    <div className="centered-message-loader" style={{ padding: "30px 0" }}>
                                        <FaSpinner
                                            style={{ width: "50px", height: "50px", color: "green" }}
                                            className="spinner-spin text-dark-500"
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className="row row-cols-1 row-cols-sm-2 row-cols-md-1 row-cols-xl-2 g-2 mb-2">
                                            <div className="col">
                                                <div className="h-100 text-center border rounded p-2 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '75px' }}>
                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                        <FaUsers style={{ fontSize: '1.2rem', color: "#01994e", }} />
                                                        <div className="fs-6 fw-semibold">{ReportSerachTransactionsData || 0}</div>
                                                    </div>
                                                    <small className="text-muted">Patient Visits</small>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <div className="h-100 text-center border rounded p-2 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '75px' }}>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <FaFileInvoice style={{
                                                            fontSize: "1rem",
                                                            color: "#01994e",
                                                        }} />
                                                        <div className="fs-6 fw-semibold">{ReportSerachReportGenrateData || 0}</div>
                                                    </div>
                                                    <small className="text-muted">Report Available</small>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row row-cols-1 g-2 mb-3">
                                            <div className="col">
                                                <div className="border rounded text-center p-2 h-100 position-relative d-flex  justify-content-center gap-2 align-items-center">
                                                    <div className="fs-6 fw-semibold text-muted">
                                                        {ReportDelayedData?.length > 0 ? (
                                                            <>
                                                                <div className="d-flex align-items-center">
                                                                    <AlarmClock
                                                                        className="vibrate mr-1"
                                                                        stroke="red"
                                                                        size={18}
                                                                    />
                                                                    <small className="text-muted">Report Delayed:</small>&nbsp;
                                                                    {ReportDelayedData.length}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            "No delayed record found"
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </>
                                )}
                            </div>
                        </div> */}

                        <TestAndRevenueComponent
                            testChartLoader={testAndRevenueLoader}
                            topTestDetails={topTestDetails}
                            setSelectedRevenueMonth={setSelectedRevenueMonth}
                        />

                        <div className="col-md-6 col-lg-4 mb-3">
                            <div className="bg-white rounded p-2 dashboard-card dashboard-card-enhanced shadow-sm h-100">
                                <div className="d-flex flex-wrap flex-xl-nowrap justify-content-between align-items-center mb-2">
                                    <h5 className="mb-2 mb-xl-0 me-3">Visit Summary</h5>

                                    <div className="bg-light rounded-pill p-1 d-flex" style={{ width: "fit-content" }}>
                                        <button
                                            title="Count"
                                            className={`btn btn-sm rounded-pill border-0 px-2 ${viewVisitsMode === 'Count'
                                                ? 'custom-sumbitbtn text-white'
                                                : 'bg-transparent text-dark'
                                                }`}
                                            onClick={() => setViewVisitsMode('Count')}
                                        >
                                            Count
                                        </button>

                                        <button
                                            title="Revenue"
                                            className={`btn btn-sm rounded-pill border-0 px-2 ${viewVisitsMode === 'Revenue'
                                                ? 'custom-sumbitbtn text-white'
                                                : 'bg-transparent text-dark'
                                                }`}
                                            onClick={() => setViewVisitsMode('Revenue')}
                                        >
                                            Revenue
                                        </button>
                                    </div>
                                </div>



                                {barChartVisitsLoader ? (
                                    <div className='centered-message-loader'>
                                        <FaSpinner
                                            style={{ width: "50px", height: "50px", color: "green" }}
                                            className="spinner-spin text-dark-500"
                                        />
                                    </div>
                                ) : (

                                    <>
                                        {ChartVisitsData && ChartVisitsData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={300}>
                                                <BarChart
                                                    data={ChartVisitsData}
                                                    margin={{ top: 10, right: 10, left: 10, bottom: 60 }}
                                                >
                                                    <CartesianGrid stroke="#e0e0e0" strokeDasharray="3 3" />
                                                    <XAxis
                                                        dataKey="MonthYear"
                                                        tick={<CustomXAxisTick />}
                                                        axisLine={{ stroke: '#ccc' }}
                                                        tickLine={false}
                                                        interval={0}
                                                        label={{
                                                            value: 'MonthYear',
                                                            position: 'insideBottom',
                                                            offset: -45,
                                                            fontSize: 12,
                                                            fill: '#555',
                                                            fontWeight: 'bold',
                                                        }}
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 10, fill: '#8884d8', fontWeight: 'bold' }}
                                                        axisLine={{ stroke: '#ccc' }}
                                                        tickLine={false}
                                                        label={{
                                                            value: viewVisitsMode === 'Count' ? 'Count' : 'Revenue',
                                                            angle: -90,
                                                            position: 'insideLeft',
                                                            offset: 0,
                                                            fontSize: 12,
                                                            fill: '#555',
                                                            fontWeight: 'bold',
                                                        }}
                                                    />
                                                    <Tooltip
                                                        content={<VisitCustomTooltip viewVisitsMode={viewVisitsMode} />}
                                                        cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                                                    />

                                                    {/* <Tooltip content={<VisitCustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }} /> */}
                                                    <Bar
                                                        dataKey={viewVisitsMode === 'Count' ? 'Count' : 'NetAmount'}
                                                        fill="#4caf50"
                                                        barSize={15}
                                                        radius={[10, 10, 0, 0]}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (

                                            <div className="alert alert-warning p-2" style={{ fontSize: '12px' }}>
                                                No records found.
                                            </div>
                                        )}
                                    </>

                                )}

                            </div>
                        </div>

                        {/* TransactionCard&Invoice */}
                        <div className="col-12 col-md-6 col-lg-4 mb-3">

                            {/* <div className="col-md-6 col-lg-4 mb-3"> */}
                            {/* <div className="bg-white rounded p-3 shadow-sm h-100"> */}

                            {(() => {

                                if (accountDetails.MHL_Payment_Type__c == "Rolling Advance") {
                                    return (
                                        <>

                                            <div className="bg-white position-relative rounded p-3 dashboard-card dashboard-card-enhanced shadow-sm h-100">
                                                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-xl-center mb-3">
                                                    <h5 className="mb-0">
                                                        Transactions
                                                        <small className="text-dark fw-bold ml-1" style={{ fontSize: '0.7rem' }}>
                                                            ({new Date().toLocaleString('en-US', { month: 'short' }) + '-' + String(new Date().getFullYear()).slice(-2)})
                                                        </small>
                                                    </h5>

                                                </div>

                                                {raTransactionLoader ? (
                                                    <div className="centered-message-loader" style={{ padding: "30px 0" }}>
                                                        <FaSpinner
                                                            style={{ width: "50px", height: "50px", color: "green" }}
                                                            className="spinner-spin text-dark-500"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="row row-cols-1 row-cols-sm-2 row-cols-md-1 row-cols-xl-2 g-2 justify-content-center align-items-stretch mb-2">

                                                        {/* Closing Balance */}
                                                        <div className="col">
                                                            <div className="h-100 text-center border rounded p-2 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '75px' }}>
                                                                <div className="d-flex align-items-center justify-content-center mb-1 fs-6 fw-semibold">
                                                                    <FaBalanceScale className="text-warning me-1" />
                                                                    ₹{RATransactionCardData.closingBalance || 0}
                                                                </div>
                                                                <div className="d-flex align-items-end justify-content-center gap-1">
                                                                    <small className="text-dark">Opening Balance</small>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Deposit */}
                                                        <div className="col">
                                                            <div className="h-100 text-center border rounded p-2 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '75px' }}>
                                                                <div className="d-flex align-items-center justify-content-center mb-1 fs-6 fw-semibold">
                                                                    <FaMoneyBillWave className="text-success me-1" />
                                                                    ₹{RATransactionCardData.totalDeposit || 0}
                                                                </div>
                                                                <small className="text-dark">Deposits</small>
                                                            </div>
                                                        </div>

                                                        {/* Current Balance */}
                                                        <div className="col">
                                                            <div className="h-100 text-center border rounded p-2 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '75px' }}>
                                                                <div className="d-flex align-items-center justify-content-center mb-1 fs-6 fw-semibold">
                                                                    <FaWallet className="text-info me-1" />
                                                                    ₹{RATransactionCardData.currentBalance || 0}
                                                                </div>
                                                                <div className="d-flex align-items-end justify-content-center gap-1">
                                                                    <small className="text-dark">Current Balance</small>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Transactions */}
                                                        <div className="col">
                                                            <div className="h-100 text-center border rounded p-2 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '75px' }}>
                                                                <div className="d-flex align-items-center justify-content-center mb-1 fs-6 fw-semibold">
                                                                    <FaExchangeAlt className="text-danger me-1" />
                                                                    ₹{RATransactionCardData.totalTransaction || 0} | {RATransactionCardData.totalTransactionEntries || 0}
                                                                </div>
                                                                <small className="text-dark">Transactions</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                        </>

                                    );
                                }
                                if (accountDetails.MHL_Payment_Type__c == "Credit") {
                                    return (
                                        <>
                                            <div className="bg-white rounded p-2 dashboard-card dashboard-card-enhanced shadow-sm h-100">
                                                <div className="d-flex justify-content-between align-items-center mb-2 pt-2">
                                                    <h5 className="mb-1">Total O/S{CCInvoiceOutstandingData?.ClientOutstandingAmt && (<small>: ₹{CCInvoiceOutstandingData.ClientOutstandingAmt}</small>)}</h5>
                                                </div>


                                                {invoiceLoader ? (
                                                    <div className="centered-message-loader" style={{ padding: "30px 0" }}>
                                                        <FaSpinner
                                                            style={{ width: "50px", height: "50px", color: "green" }}
                                                            className="spinner-spin text-dark-500"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="card border mb-1" style={{ fontSize: '12px' }}>
                                                        <div className="card-header fw-bold py-1 d-flex justify-content-between align-items-center">
                                                            <span>Last 3 months invoices</span>
                                                        </div>

                                                        <div

                                                            className="px-2"
                                                            style={{
                                                                maxHeight: invoices.length > 2 ? '250px' : 'auto',
                                                                overflowY: invoices.length > 2 ? 'auto' : 'visible',
                                                            }}
                                                        >
                                                            {invoices.map((invoice, idx) => (
                                                                <div key={idx}>
                                                                    <div className="border-bottom py-2 px-1">
                                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                                            <div>
                                                                                <strong>Inv No:</strong> {invoice.invoiceNumber}

                                                                            </div>
                                                                            <span className="btn btn-sm pay-nowbtn" style={{
                                                                                fontSize: "8px",
                                                                                position: "relative",
                                                                                top: "-2px" // adjust value to control how far up you want it
                                                                            }}>{invoice.invoiceStatus}</span>
                                                                            {/* {invoice.freepayQRCodeLink && (
                                                                                <button
                                                                                    onClick={() =>
                                                                                        window.open(invoice.freepayQRCodeLink, "_blank")
                                                                                    }
                                                                                    className="btn btn-sm pay-nowbtn"
                                                                                    style={{
                                                                                        fontSize: "8px",
                                                                                        position: "relative",
                                                                                        top: "-2px" // adjust value to control how far up you want it
                                                                                    }}
                                                                                >
                                                                                    Pay Now
                                                                                </button>

                                                                            )} */}
                                                                        </div>

                                                                        <div className="row mt-1">
                                                                            <div className="col-6">
                                                                                <strong>Inv Date:</strong> {invoice.invoiceDate}
                                                                            </div>
                                                                            <div className="col-6">
                                                                                <strong>Due Date:</strong> {invoice.invoiceDueDate}
                                                                            </div>
                                                                        </div>

                                                                        <div className="row mt-1">
                                                                            <div className="col-6">
                                                                                <strong>Inv Amt:</strong> ₹{invoice.invoiceAmount}
                                                                            </div>
                                                                            <div className="col-6">
                                                                                <strong>O/S Amt:</strong> ₹{invoice.invoiceOpenAmount}
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div >

                                        </>);

                                }

                            })()}
                        </div >

                    </div>

                )}

            </ div>
            ) : (
                <div className="text-danger">
                    <h5>
                        Invalid or expired link.
                    </h5></div>
            )
            }
        </div >
    );
};

export default MHLSalesComponent;
